import { useEffect, useState } from "react";
import { Check, Sparkles, CreditCard, Info } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

export default function Pricing() {
  const { lang } = useLang();
  const [plans, setPlans] = useState([]);
  const [current, setCurrent] = useState("free_trial");
  const [testMode, setTestMode] = useState(true);
  const [pending, setPending] = useState(null);

  useEffect(() => {
    api.get("/subscription/plans").then(({ data }) => setPlans(data)).catch(() => {});
    api.get("/subscription/me").then(({ data }) => setCurrent(data.plan_id)).catch(() => {});
    api.get("/payments/config").then(({ data }) => setTestMode(data.test_mode)).catch(() => {});
  }, []);

  const proceedCheckout = async (plan) => {
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: plan.lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  const select = async (plan) => {
    if (plan.id === "free_trial") {
      try { await api.post("/subscription/select", { plan_id: "free_trial" }); setCurrent("free_trial"); toast.success(lang === "es" ? "Plan actualizado." : "Plan updated."); }
      catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
      return;
    }
    if (!plan.lookup_key) {
      toast.info(lang === "es" ? "Precio personalizado — contáctanos en hello@brilla.com" : "Custom pricing — contact us at hello@brilla.com");
      return;
    }
    setPending(plan); // open the checkout hand-off dialog
  };

  const individual = plans.filter((p) => p.tier === "individual");
  const program = plans.filter((p) => p.tier === "program");

  const Card = ({ p }) => (
    <div className={`rounded-3xl border p-6 flex flex-col ${current === p.id ? "border-secondary ring-2 ring-secondary/30" : "border-border"} bg-card`} data-testid={`plan-${p.id}`}>
      <h3 className="font-heading font-bold text-xl">{p.name}</h3>
      <div className="mt-2 flex items-baseline gap-1">
        <span className="font-heading font-extrabold text-3xl">{p.price}</span>
        <span className="text-muted-foreground">{p.cadence}</span>
      </div>
      <ul className="mt-4 space-y-2 flex-1">
        {p.features.map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-sm"><Check className="h-4 w-4 text-primary mt-0.5 shrink-0" /> {f}</li>
        ))}
      </ul>
      <Button onClick={() => select(p)} disabled={current === p.id}
        className={`mt-6 rounded-full ${current === p.id ? "" : "bg-secondary hover:bg-secondary/90"}`} variant={current === p.id ? "outline" : "default"}
        data-testid={`select-${p.id}`}>
        {current === p.id ? (lang === "es" ? "Plan actual" : "Current plan") : (p.lookup_key ? (lang === "es" ? "Suscribirse" : "Subscribe") : (p.id === "free_trial" ? (lang === "es" ? "Elegir" : "Choose") : (lang === "es" ? "Contáctanos" : "Contact us")))}
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        <div className="space-y-2">
          <Badge className="rounded-full bg-accent/15 text-accent"><Sparkles className="h-3.5 w-3.5" /> {lang === "es" ? "Planes" : "Plans"}</Badge>
          <h1 className="font-heading font-extrabold text-3xl tracking-tight">{lang === "es" ? "Precios y Suscripciones" : "Pricing & Subscriptions"}</h1>
          <p className="text-muted-foreground">{lang === "es" ? "Elige el plan adecuado para ti o tu programa. Pago seguro con Stripe." : "Choose the right plan for you or your program. Secure checkout with Stripe."}</p>
        </div>

        <section className="space-y-3">
          <h2 className="font-heading font-bold text-xl">{lang === "es" ? "Educador individual" : "Individual Educator"}</h2>
          <div className="grid md:grid-cols-3 gap-4">{individual.map((p) => <Card key={p.id} p={p} />)}</div>
        </section>
        <section className="space-y-3">
          <h2 className="font-heading font-bold text-xl">{lang === "es" ? "Programa / Centro" : "Program / Center"}</h2>
          <div className="grid md:grid-cols-3 gap-4">{program.map((p) => <Card key={p.id} p={p} />)}</div>
        </section>

        <Dialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><CreditCard className="h-5 w-5 text-primary" /> {lang === "es" ? "Ir al pago seguro" : "Continue to secure checkout"}</DialogTitle>
              <DialogDescription>
                {lang === "es" ? `Te llevaremos a la página segura de Stripe para suscribirte a ${pending?.name}.` : `You'll be taken to Stripe's secure page to subscribe to ${pending?.name}.`}
              </DialogDescription>
            </DialogHeader>
            {testMode && (
              <div className="rounded-xl border border-accent/30 bg-accent/5 p-4 space-y-2 text-sm" data-testid="test-card-guide">
                <p className="flex items-center gap-2 font-semibold text-accent"><Info className="h-4 w-4" /> {lang === "es" ? "Modo de prueba — usa esta tarjeta" : "Test mode — use this card"}</p>
                <div className="font-mono text-foreground space-y-0.5">
                  <p>{lang === "es" ? "Tarjeta:" : "Card:"} <span className="font-bold">4242 4242 4242 4242</span></p>
                  <p>{lang === "es" ? "Vence:" : "Expiry:"} {lang === "es" ? "cualquier fecha futura" : "any future date"} · CVC: {lang === "es" ? "cualquiera" : "any"} · ZIP: {lang === "es" ? "cualquiera" : "any"}</p>
                </div>
                <p className="text-muted-foreground text-xs">{lang === "es" ? "No se realizará ningún cargo real en modo de prueba." : "No real charge is made in test mode."}</p>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" className="rounded-full" onClick={() => setPending(null)} data-testid="checkout-cancel">{lang === "es" ? "Cancelar" : "Cancel"}</Button>
              <Button className="rounded-full bg-secondary hover:bg-secondary/90" onClick={() => proceedCheckout(pending)} data-testid="checkout-continue">{lang === "es" ? "Continuar" : "Continue"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
